import { ThemeEvents } from '@theme/events';

const CART_MUTATION_PATHS = ['/cart/add', '/cart/change', '/cart/update', '/cart/clear'];

function isCartMutation(url) {
  try {
    const pathname = new URL(url, location.origin).pathname;
    return CART_MUTATION_PATHS.some((path) => pathname.startsWith(path));
  } catch {
    return false;
  }
}

class ShippingBar extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal });
    this.#interceptCartRequests(signal);
    this.#fetchCartAndUpdate();
    this.#updateDeliveryCountdown();
    this.#startCountdownTimer();
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  get #threshold() {
    return Number(this.dataset.threshold) || 0;
  }

  get #cutoffHour() {
    return Number(this.dataset.cutoffHour) || 15;
  }

  get #deliveryMin() {
    return Number(this.dataset.deliveryMin) || 3;
  }

  get #deliveryMax() {
    return Number(this.dataset.deliveryMax) || 7;
  }

  #handleCartUpdate = () => {
    this.#fetchCartAndUpdate();
  };

  #interceptCartRequests(signal) {
    if (ShippingBar.#intercepted) return;
    ShippingBar.#intercepted = true;

    const triggerUpdate = () => {
      document.dispatchEvent(new Event('shipping-bar:cart-change'));
    };

    // Intercept fetch
    const origFetch = window.fetch;
    window.fetch = function (...args) {
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
      const result = origFetch.apply(this, args);
      if (isCartMutation(url)) {
        result.then(() => triggerUpdate()).catch(() => {});
      }
      return result;
    };

    // Intercept XMLHttpRequest
    const origXhrOpen = XMLHttpRequest.prototype.open;
    const origXhrSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      this.__fsbUrl = typeof url === 'string' ? url : '';
      return origXhrOpen.call(this, method, url, ...rest);
    };

    XMLHttpRequest.prototype.send = function (...args) {
      if (isCartMutation(this.__fsbUrl)) {
        this.addEventListener('load', () => triggerUpdate(), { once: true });
      }
      return origXhrSend.apply(this, args);
    };

    document.addEventListener('shipping-bar:cart-change', this.#handleCartUpdate, { signal });
  }

  static #intercepted = false;

  async #fetchCartAndUpdate() {
    try {
      const response = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
      });
      const cart = await response.json();
      this.#updateShipping(cart.total_price);
    } catch {
      // Silently fail
    }
  }

  #updateShipping(totalPrice) {
    const message = this.querySelector('[data-ref="message"]');
    const remaining = this.querySelector('[data-ref="remaining"]');
    const remainingAmount = this.querySelector('[data-ref="remainingAmount"]');
    const progress = this.querySelector('[data-ref="progress"]');
    const track = this.querySelector('[data-ref="track"]');
    const qualifiedMessage = this.querySelector('[data-ref="qualifiedMessage"]');
    const defaultMessage = this.querySelector('[data-ref="defaultMessage"]');

    if (!progress || !track) return;

    const threshold = this.#threshold;
    const percentage = threshold > 0 ? Math.min((totalPrice / threshold) * 100, 100) : 100;
    const isQualified = totalPrice >= threshold;

    progress.style.width = `${percentage}%`;
    track.setAttribute('aria-valuenow', String(totalPrice));

    if (isQualified) {
      if (message) message.textContent = qualifiedMessage?.content?.textContent?.trim() || '';
      if (remaining) remaining.hidden = true;
    } else {
      const diff = threshold - totalPrice;
      const formatted = this.#formatMoney(diff);

      if (message) message.textContent = defaultMessage?.content?.textContent?.trim() || '';
      if (remainingAmount) remainingAmount.textContent = formatted;
      if (remaining) remaining.hidden = false;
    }
  }

  #updateDeliveryCountdown() {
    const delivery = this.querySelector('[data-ref="delivery"]');
    if (!delivery) return;

    const deliveryTemplate = this.querySelector('[data-ref="deliveryTemplate"]');
    const deliveryLeftText = deliveryTemplate?.content?.textContent?.trim() || 'left for delivery';

    const now = new Date();
    const cutoff = new Date(now);
    cutoff.setHours(this.#cutoffHour, 0, 0, 0);

    if (now >= cutoff) {
      cutoff.setDate(cutoff.getDate() + 1);
    }

    const diff = cutoff - now;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    const locale = document.documentElement.lang || 'en';
    const startDate = new Date(cutoff);
    startDate.setDate(startDate.getDate() + this.#deliveryMin);
    const endDate = new Date(cutoff);
    endDate.setDate(endDate.getDate() + this.#deliveryMax);

    const formatDate = (date) => {
      return date.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
    };

    const timeLeft = `<strong>${hours}h ${minutes}m</strong>`;
    const dateRange = `<strong>${formatDate(startDate)}–${formatDate(endDate)}</strong>`;

    delivery.innerHTML = `${timeLeft} ${deliveryLeftText} ${dateRange}`;
  }

  #startCountdownTimer() {
    const { signal } = this.#controller;
    const interval = setInterval(() => this.#updateDeliveryCountdown(), 60_000);
    signal.addEventListener('abort', () => clearInterval(interval));
  }

  #formatMoney(amountInCents) {
    const currency = this.dataset.currency || 'USD';
    const amount = amountInCents / 100;

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    } catch {
      return `${amount} ${currency}`;
    }
  }
}

customElements.define('shipping-bar', ShippingBar);
